import React, { useMemo } from 'react'
import { Input, InputProps, Select, Space } from 'antd'
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/min'
import { MobileNumber, parseMobileNumber } from '../phone-number'

type MobileNumberInputProps = Pick<InputProps, 'id' | 'aria-describedby' | 'aria-required' | 'aria-invalid'> & {
    value?: MobileNumber
    onChange?: (value: MobileNumber) => void
    locale: string
    countryLabel: string
    numberLabel: string
}

export function MobileNumberInput({
    value,
    onChange,
    locale,
    countryLabel,
    numberLabel,
    ...inputProps
}: MobileNumberInputProps) {
    const country = value?.country ?? 'NL'
    const options = useMemo(() => {
        const names = new Intl.DisplayNames([locale.replace('_', '-')], { type: 'region' })
        return getCountries()
            .map((code) => {
                const prefix = `+${getCountryCallingCode(code)}`
                return {
                    value: code,
                    label: `${names.of(code)} (${prefix})`,
                    shortLabel: `${code} ${prefix}`,
                    search: `${names.of(code)} ${code} ${prefix}`,
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label, locale.replace('_', '-')))
    }, [locale])
    return (
        <Space.Compact className="w-full">
            <Select
                className="w-[126px] shrink-0"
                aria-label={countryLabel}
                value={country}
                options={options}
                optionLabelProp="shortLabel"
                showSearch
                optionFilterProp="search"
                popupMatchSelectWidth={320}
                onChange={(country) => onChange?.({ country, number: value?.number ?? '' })}
            />
            <Input
                {...inputProps}
                className="min-w-0"
                aria-label={numberLabel}
                type="tel"
                autoComplete="tel-national"
                maxLength={40}
                value={value?.number ?? ''}
                onChange={(event) => {
                    const number = event.target.value
                    const parsed = parseMobileNumber({ country, number })
                    if (/^(\+|00)/.test(number.trim()) && parsed?.isPossible()) {
                        const detectedCountry =
                            parsed.country ??
                            (getCountryCallingCode(country) === parsed.countryCallingCode
                                ? country
                                : getCountries().find(
                                      (code) => getCountryCallingCode(code) === parsed.countryCallingCode
                                  ))
                        if (detectedCountry) onChange?.({ country: detectedCountry, number: parsed.formatNational() })
                        else onChange?.({ country, number })
                    } else onChange?.({ country, number })
                }}
            />
        </Space.Compact>
    )
}
